<template>
  <Teleport to="body">
    <Transition name="modal">
        <div
          v-if="isOpen"
          ref="modalRef"
          class="fixed inset-0 z-[9999] bg-black"
          @click.self="closeModal()"
          tabindex="-1"
        >
        <!-- Full Screen Image Gallery -->
        <div class="absolute inset-0 w-full h-full">
          <Swiper
            :modules="modules"
            :slides-per-view="1"
            :loop="true"
            :autoplay="{
              delay: 6000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }"
            :pagination="{
              el: '.swiper-pagination-custom',
              clickable: true
            }"
            :speed="500"
            :touch-events-target="'container'"
            class="h-full w-full"
            @swiper="onSwiper"
            @slideChange="onSlideChange"
          >
            <!-- Product gallery images -->
            <SwiperSlide
              v-for="(imagePath, index) in productImages"
              :key="index"
              class="h-full"
            >
              <div class="relative w-full h-full">
                <template v-if="resolvedImages[index]?.provider">
                  <NuxtImg
                    v-bind="resolvedImages[index]"
                    :provider="resolvedImages[index].provider"
                    :alt="`${product.name} - Image ${index + 1}`"
                    class="w-full h-full object-cover"
                    sizes="100vw"
                    :modifiers="{ quality: 'auto', format: 'auto' }"
                  />
                </template>
                <img
                  v-else
                  :src="resolvedImages[index]?.src || imagePath"
                  :alt="`${product.name} - Image ${index + 1}`"
                  class="w-full h-full object-cover"
                />
              </div>
            </SwiperSlide>
          </Swiper>
          
          <!-- Dark overlay for better text readability -->
          <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none z-10"></div>
          
          <!-- Custom Pagination Indicators -->
          <div class="swiper-pagination-custom absolute top-1/2 left-4 transform -translate-y-1/2 z-15 flex flex-col gap-2"></div>
        </div>

        <!-- Close Button -->
        <button
          @click="closeModal"
          class="absolute top-4 right-4 z-20 w-12 h-12 bg-white/90 hover:bg-white backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
          :aria-label="t('pages.collection.imagePreview.close')"
        >
          <svg class="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Product Info Overlay -->
        <div
          class="absolute bottom-0 left-0 right-0 z-10 p-6 sm:p-8 lg:p-12"
          @click.stop
        >
          <div class="max-w-4xl mx-auto space-y-6">
            <div v-if="product.material" class="text-white rounded uppercase text-sm font-light tracking-[0.5rem] -mb-4">
              <span class="font-serif lowercase text-lg tracking-widest">ommi.shop</span> {{ product.material }} 
            </div>
            <!-- Product Name and Artist -->
            <div class="space-y-2">
              <h2 class="text-3xl sm:text-4xl lg:text-5xl font-medium text-white drop-shadow-lg">{{ product.name }}</h2>
              <p class="text-xl sm:text-2xl text-white/90 drop-shadow-md">{{ product.brand }}</p>
            </div>

            <!-- Price and Material -->
            <div class="flex flex-wrap items-center gap-4">
              <div v-if="displayPrice" class="flex items-baseline gap-2">
                <span v-if="originalPrice && originalPrice !== displayPrice" class="text-white/60 line-through text-lg">
                  € {{ formatPrice(originalPrice) }}
                </span>
                <span class="text-2xl sm:text-3xl font-semibold text-white drop-shadow-lg">€ {{ formatPrice(displayPrice) }}</span>
              </div>
              <!-- Sold Out Badge -->
              <div v-if="isSoldOut" class="px-6 py-8 bg-red-600 text-white rounded-full uppercase text-sm font-medium">
                {{ t('pages.collection.productCard.soldBadge') }}
              </div>
            </div>

            <!-- CTAs -->
            <div class="flex flex-col sm:flex-row gap-4 pt-2">
              <!-- Buy Now Button -->
              <div class="relative inline-block group order-2 md:order-1">
                <div v-if="!isSoldOut" class="absolute inset-0 rounded bg-light-brown border border-black transition-transform duration-150 z-20"></div>
                <div v-if="!isSoldOut" class="absolute inset-0 rounded bg-light-brown-800 border border-black transition-transform duration-150 group-hover:-translate-x-2 group-hover:translate-y-2 z-10"></div>
                <button
                  @click="handleBuyNowClick"
                  :disabled="isSoldOut"
                  :class="[
                    'relative inline-flex rounded no-underline items-center justify-center border border-black transition-all duration-150 z-30 w-full sm:w-auto h-14 px-8 text-xl font-medium',
                    isSoldOut
                      ? 'bg-dark-teal-600 text-white opacity-20'
                      : 'bg-dark-teal-600 text-white group-hover:-translate-x-2 group-hover:-translate-y-2'
                  ]"
                >
                  {{ t('pages.collection.imagePreview.cta.buyNow') }}
                </button>
              </div>
              <!-- Catalog Button -->
              <div class="relative inline-block group order-1 md:order-2">
                <button
                  @click="handleCatalogClick"
                  :disabled="isSoldOut"
                  :class="[
                    'relative inline-flex rounded no-underline items-center justify-center transition-all duration-150 z-30 w-full sm:w-auto h-14 px-8 text-xl font-medium',
                    isSoldOut
                      ? 'text-white opacity-30'
                      : 'text-white'
                  ]"
                >
                  {{ t('pages.collection.imagePreview.cta.catalog') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from '#imports'
import { useImageCdn } from '~/composables/useImageCdn'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'

interface CollectionProduct {
  id: number | string
  brand: string
  name: string
  description: string
  material: string
  price: string | number
  images?: string[]
  image?: string // Legacy support
  status?: string
  sku?: {
    discountedPrice?: number | string
    price?: number | string
    slugEn?: string
  }
}

const props = defineProps<{
  isOpen: boolean
  product: CollectionProduct | null
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { resolveForDisplay } = useImageCdn()
const modalRef = ref<HTMLElement | null>(null)
const swiperInstance = ref<any>(null)

// Swiper modules
const modules = [Autoplay, Pagination]

// Get product images array (fallback to single image for backward compatibility)
const productImages = computed(() => {
  if (!props.product) return []
  // Use images array if available, otherwise fallback to single image
  if (Array.isArray(props.product.images) && props.product.images.length > 0) {
    return props.product.images
  }
  // Legacy support: use image property if images array doesn't exist
  if (props.product.image) {
    return [props.product.image]
  }
  return []
})

// Resolve all images for the gallery
const resolvedImages = computed(() => {
  return productImages.value.map(imagePath => {
    if (!imagePath) return { src: '', provider: undefined }
    return resolveForDisplay(imagePath, props.product?.sku?.slugEn)
  })
})

// Legacy support: resolvedImage for backward compatibility
const resolvedImage = computed(() => {
  if (!props.product) return { src: '', provider: undefined }
  const firstImage = productImages.value[0]
  if (!firstImage) return { src: '', provider: undefined }
  return resolveForDisplay(firstImage, props.product.sku?.slugEn)
})

// Swiper event handlers
const onSwiper = (swiper: any) => {
  swiperInstance.value = swiper
}

const onSlideChange = () => {
  // Update custom pagination when slide changes
  updateCustomPagination()
}

// Update custom pagination indicators
const updateCustomPagination = () => {
  if (!swiperInstance.value || !import.meta.client) return
  
  const paginationEl = document.querySelector('.swiper-pagination-custom') as HTMLElement
  if (!paginationEl) return
  
  const activeIndex = swiperInstance.value.realIndex
  const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet')
  
  bullets.forEach((bullet, index) => {
    const bulletEl = bullet as HTMLElement
    if (index === activeIndex) {
      bulletEl.classList.add('active')
      bulletEl.classList.remove('inactive')
    } else {
      bulletEl.classList.remove('active')
      bulletEl.classList.add('inactive')
    }
  })
}

// Get number of slides for pagination
const slideCount = computed(() => productImages.value.length)

// Handle keyboard events
const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.isOpen) {
    closeModal()
  } else if (event.key === 'ArrowLeft' && props.isOpen && swiperInstance.value) {
    // Navigate to previous slide
    event.preventDefault()
    swiperInstance.value.slidePrev()
    updateCustomPagination()
  } else if (event.key === 'ArrowRight' && props.isOpen && swiperInstance.value) {
    // Navigate to next slide
    event.preventDefault()
    swiperInstance.value.slideNext()
    updateCustomPagination()
  }
}

// Watch for modal open/close to manage focus, keyboard listeners, and body scroll
watch(() => props.isOpen, (isOpen) => {
  if (isOpen && import.meta.client) {
    // Prevent body scroll
    document.body.style.overflow = 'hidden'
    // Focus the modal for keyboard navigation
    setTimeout(() => {
      modalRef.value?.focus()
      // Update pagination after Swiper initializes
      setTimeout(() => {
        updateCustomPagination()
      }, 100)
    }, 100)
    // Add keyboard listener
    document.addEventListener('keydown', handleKeyDown)
  } else if (import.meta.client) {
    // Restore body scroll
    document.body.style.overflow = ''
    // Remove keyboard listener
    document.removeEventListener('keydown', handleKeyDown)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    document.removeEventListener('keydown', handleKeyDown)
    // Ensure body scroll is restored
    document.body.style.overflow = ''
  }
})

const displayPrice = computed(() => {
  if (props.product?.sku?.discountedPrice) {
    return props.product.sku.discountedPrice
  }
  return props.product?.price
})

const originalPrice = computed(() => {
  if (props.product?.sku?.discountedPrice && props.product.sku.price) {
    return props.product.sku.price
  }
  return undefined
})

const formatPrice = (price: string | number | undefined): string => {
  if (!price) return '0.00'
  const numPrice = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.,]/g, '').replace(',', '.')) : price
  if (isNaN(numPrice)) return String(price)
  return numPrice.toFixed(2)
}

const isSoldOut = computed(() => {
  return props.product?.status === 'sold-out'
})

const closeModal = () => {
  // Remove hash from URL when closing
  if (import.meta.client && window.location.hash) {
    const newUrl = window.location.href.split('#')[0]
    window.history.replaceState(null, '', newUrl)
  }
  emit('close')
}

const handleCatalogClick = () => {
  if (isSoldOut.value) return
  alert(t('pages.collection.imagePreview.catalogAlert.message'))
}

const handleBuyNowClick = () => {
  if (isSoldOut.value) return
  alert(t('pages.collection.imagePreview.buyNowAlert.message'))
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.4s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div:first-child,
.modal-leave-active > div:first-child {
  transition: transform 0.4s ease, opacity 0.4s ease;
}

.modal-enter-from > div:first-child,
.modal-leave-to > div:first-child {
  transform: scale(1.05);
  opacity: 0;
}

/* Custom Swiper Pagination Styles */
:deep(.swiper-pagination-custom) {
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 15;
  width: auto;
}

:deep(.swiper-pagination-custom .swiper-pagination-bullet) {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background-color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 1;
  margin: 0;
}

:deep(.swiper-pagination-custom .swiper-pagination-bullet-active),
:deep(.swiper-pagination-custom .swiper-pagination-bullet.active) {
  width: 2rem;
  background-color: white;
}

:deep(.swiper-pagination-custom .swiper-pagination-bullet:hover) {
  background-color: rgba(255, 255, 255, 0.6);
}

/* Swiper container styles */
:deep(.swiper) {
  width: 100%;
  height: 100%;
}

:deep(.swiper-slide) {
  width: 100%;
  height: 100%;
}
</style>
